import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Trash2,
  Mail,
  MailOpen,
  Archive,
  Sparkles,
  HandCoins,
} from "lucide-react";
import { api } from "@/lib/api";
import type { ContactMessage } from "@shared/schema";

export function AdminMessages() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["admin", "messages", { archived: showArchived }],
    queryFn: () =>
      api<ContactMessage[]>(
        `/api/admin/messages${showArchived ? "?archived=1" : ""}`,
      ),
  });

  const update = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { isRead?: boolean; isArchived?: boolean };
    }) =>
      api(`/api/admin/messages/${id}`, { method: "PATCH", body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "messages"] }),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/messages/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "messages"] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("admin.messages.title")}</h1>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          {t("admin.messages.showArchived")}
        </label>
      </div>

      <div className="mt-6 card overflow-hidden">
        {q.isLoading && (
          <div className="p-6 text-brand-500">{t("common.loading")}</div>
        )}
        {q.data && q.data.length === 0 && (
          <div className="p-6 text-brand-500">{t("admin.messages.empty")}</div>
        )}
        {q.data && (
          <ul className="divide-y divide-brand-100">
            {q.data.map((m) => {
              const expanded = openId === m.id;
              const name = (m.payload?.name as string) || "—";
              const summary =
                (m.payload?.message as string) ||
                Object.values(m.payload || {})
                  .filter((v) => typeof v === "string")
                  .join(" · ")
                  .slice(0, 80);
              return (
                <li
                  key={m.id}
                  className={!m.isRead ? "bg-accent-50/30" : undefined}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setOpenId(expanded ? null : m.id);
                      if (!m.isRead) {
                        update.mutate({ id: m.id, data: { isRead: true } });
                      }
                    }}
                    className="w-full p-4 text-left flex items-start gap-3 hover:bg-brand-50/50"
                  >
                    {m.isRead ? (
                      <MailOpen size={18} className="text-brand-400 mt-0.5" />
                    ) : (
                      <Mail size={18} className="text-accent-700 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{name}</span>
                        {!m.isRead && (
                          <span className="text-xs bg-accent-100 text-accent-700 px-1.5 py-0.5 rounded">
                            új
                          </span>
                        )}
                        <span className="text-xs text-brand-500 ml-auto whitespace-nowrap">
                          {new Date(m.createdAt).toLocaleString("hu-HU")}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-brand-600 truncate">
                        {summary}
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 pb-4 -mt-1">
                      <div className="ml-7 pl-4 border-l-2 border-brand-200 space-y-3">
                        <div className="overflow-x-auto">
                          <table className="text-sm">
                            <tbody>
                              {Object.entries(m.payload || {}).map(([k, v]) => (
                                <tr key={k}>
                                  <td className="pr-4 py-1 text-brand-500 font-mono text-xs align-top">
                                    {k}
                                  </td>
                                  <td className="py-1 text-brand-800 whitespace-pre-wrap">
                                    {typeof v === "string"
                                      ? v
                                      : JSON.stringify(v)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="flex flex-wrap gap-2 text-xs text-brand-500">
                          {m.source === "ai_chat" && (
                            <span className="px-2 py-1 bg-accent-100 text-accent-700 rounded inline-flex items-center gap-1">
                              <Sparkles size={12} /> AI chatből
                            </span>
                          )}
                          {m.source === "mortgage" && (
                            <span className="px-2 py-1 bg-accent-100 text-accent-700 rounded inline-flex items-center gap-1">
                              <HandCoins size={12} /> Hitelügyintézés
                            </span>
                          )}
                          {m.projectId && (
                            <span className="px-2 py-1 bg-brand-100 rounded">
                              Projekt-érdeklődés
                            </span>
                          )}
                          {m.unitId && (
                            <span className="px-2 py-1 bg-accent-100 text-accent-700 rounded">
                              Lakás-érdeklődés
                            </span>
                          )}
                          {m.sourceUrl && (
                            <a
                              href={m.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-brand-900"
                            >
                              Forrás ↗
                            </a>
                          )}
                        </div>

                        <div className="flex gap-2 pt-2">
                          <button
                            type="button"
                            onClick={() =>
                              update.mutate({
                                id: m.id,
                                data: { isRead: !m.isRead },
                              })
                            }
                            className="btn-secondary text-xs"
                          >
                            {m.isRead ? "Olvasatlan" : "Olvasott"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              update.mutate({
                                id: m.id,
                                data: { isArchived: !m.isArchived },
                              })
                            }
                            className="btn-secondary text-xs"
                          >
                            <Archive size={14} />
                            {m.isArchived ? "Visszaállít" : "Archivál"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm("Véglegesen törli?"))
                                del.mutate(m.id);
                            }}
                            className="btn-danger text-xs"
                          >
                            <Trash2 size={14} />
                            Törlés
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
